import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Network, ArrowLeft, Search, Eye, TrendingUp } from "lucide-react";
import { getKnowledgeGraphTriples, getKnowledgeGraphStats } from "../lib/api";

export default function KnowledgeGraph() {
  const [triples, setTriples] = useState([]);
  const [stats, setStats] = useState(null);
  const [subjectQuery, setSubjectQuery] = useState("");
  const [predicateQuery, setPredicateQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      getKnowledgeGraphTriples({
        subject: subjectQuery || undefined,
        predicate: predicateQuery || undefined,
        limit: 100,
      }),
      getKnowledgeGraphStats(),
    ])
      .then(([t, s]) => {
        setTriples(t?.triples || []);
        setStats(s);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [subjectQuery, predicateQuery]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <Link
          to="/"
          data-testid="graph-back"
          className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-emerald-300 mb-6"
        >
          <ArrowLeft className="h-3 w-3" /> home
        </Link>

        <div className="border-b border-zinc-900 pb-5 mb-6">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-emerald-300 mb-2">
            <Network className="h-3 w-3" /> Neuro-symbolic library
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-[0.05em]">
            The truth graph.
          </h1>
          <p className="text-zinc-400 text-sm mt-3 max-w-3xl leading-relaxed">
            Every fact anchored by the Obsidian Sink or manually confirmed
            by a verifier is automatically decomposed into
            (subject → predicate → object) triples. The library is
            neural (Chroma-indexed for semantic search) and symbolic
            (compositional so new inferences can be derived). Every triple
            carries a receipt back to the sealed report that minted it.
            Built one HMAC-signed truth at a time. Nothing was hand-typed.
          </p>
        </div>

        {stats && (
          <div className="grid gap-3 sm:grid-cols-3 mb-6">
            <div className="border border-emerald-500/30 bg-[#0b0b0d] p-4">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Total triples</div>
              <div className="font-mono text-2xl text-emerald-300">{stats.total_triples}</div>
            </div>
            <div className="border border-fuchsia-500/30 bg-[#0b0b0d] p-4">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Deep-dives sealed</div>
              <div className="font-mono text-2xl text-fuchsia-300">{stats.total_deep_dives}</div>
            </div>
            <div className="border border-zinc-800 bg-[#0b0b0d] p-4">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Top predicate</div>
              <div className="font-mono text-2xl text-zinc-200">
                {stats.top_predicates?.[0]?.predicate || "—"}
              </div>
            </div>
          </div>
        )}

        {stats?.top_predicates?.length > 0 && (
          <div className="border border-zinc-800 bg-[#0b0b0d] p-4 mb-6">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
              <TrendingUp className="h-3 w-3" /> Top predicates
            </div>
            <div className="flex flex-wrap gap-2">
              {stats.top_predicates.map((p) => (
                <button
                  key={p.predicate}
                  onClick={() => { setPredicateQuery(p.predicate); setSubjectQuery(""); }}
                  data-testid={`filter-predicate-${p.predicate}`}
                  className={`px-2 py-1 text-[10px] uppercase tracking-widest border transition-colors ${
                    predicateQuery === p.predicate
                      ? "border-emerald-500 text-emerald-300"
                      : "border-zinc-800 text-zinc-400 hover:border-emerald-500/40 hover:text-emerald-300"
                  }`}
                >
                  {p.predicate} · {p.count}
                </button>
              ))}
              {predicateQuery && (
                <button
                  onClick={() => setPredicateQuery("")}
                  data-testid="clear-predicate-filter"
                  className="px-2 py-1 text-[10px] uppercase tracking-widest text-rose-300 hover:text-rose-100"
                >
                  clear
                </button>
              )}
            </div>
          </div>
        )}

        {/* Subject search */}
        <div className="border border-zinc-800 bg-[#0b0b0d] p-3 mb-6 flex gap-2">
          <Search className="h-4 w-4 text-zinc-500 self-center shrink-0 ml-1" />
          <input
            type="text"
            value={subjectQuery}
            onChange={(e) => setSubjectQuery(e.target.value)}
            placeholder="filter by subject (e.g. edison, alexandria, 1918)"
            data-testid="graph-subject-search"
            className="flex-1 bg-transparent outline-none text-sm text-zinc-100 placeholder-zinc-600 font-mono"
          />
        </div>

        {/* Triples */}
        {loading && triples.length === 0 ? (
          <div className="text-zinc-500 text-[11px] uppercase tracking-widest text-center py-8">
            Loading library…
          </div>
        ) : triples.length === 0 ? (
          <div className="text-zinc-600 text-[11px] uppercase tracking-widest text-center py-8 border border-dashed border-zinc-800">
            No triples yet · run a deep-dive on <Link to="/obsidian" className="text-fuchsia-300 hover:text-fuchsia-100">/obsidian</Link> to seed the library
          </div>
        ) : (
          <div className="border border-zinc-800 bg-[#0b0b0d] overflow-hidden" data-testid="graph-triples-list">
            {triples.map((t) => (
              <div
                key={t.id}
                data-testid={`triple-${t.id}`}
                className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr_auto] gap-2 sm:gap-3 items-center px-4 py-2 border-b border-zinc-900 last:border-b-0 text-sm hover:bg-zinc-950/60 transition-colors"
              >
                <div className="text-zinc-100 font-mono truncate" title={t.subject}>{t.subject}</div>
                <div className="text-emerald-300 font-mono text-[10px] uppercase tracking-widest">{t.predicate}</div>
                <div className="text-zinc-300 font-mono truncate" title={t.object}>{t.object}</div>
                <div className="text-[9px] uppercase tracking-widest text-zinc-600 font-mono hidden sm:block" title={`report ${t.source_report_id || t.source_citation_id}`}>
                  {(t.provenance_receipt_hash || "").slice(0, 8)}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="text-center mt-8">
          <Link
            to="/obsidian"
            data-testid="graph-to-obsidian"
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-fuchsia-300"
          >
            <Eye className="h-3 w-3" /> Feed the library · run a deep-dive →
          </Link>
        </div>
      </div>
    </div>
  );
}
